/**
 * HTTP client for outbound email records (`/emails/`).
 *
 * Where: consumed by `EmailsList` and email-related UI (`StreamDetails`, `LeadDetails`).
 * Uses: shared `apiClient` (`api/client.ts`).
 * Contract: GET `/emails/` → `response.data.output` cast to `ListResponse<EmailListItem>`; DELETE `/emails/:uuid/`.
 * Behavior: array query params use `paramsSerializer` (`key[]`); default `page_size` 50 and sort `send_date` desc in client.
 */
import { apiClient } from './client';

export interface EmailListItem {
  uuid?: string;
  key?: string;
  to: string;
  from_name: string | null;
  from_email: string | null;
  subject: string | null;
  message: string | null;
  status: string | null;
  type: string | null;
  send_date: string | null;
  prospect: {
    uuid?: string | null;
    key?: string | null;
    name?: string | null;
    full_name?: string | null;
    first_name?: string | null;
    last_name?: string | null;
    profile_image_url?: string | null;
    profile_image?: string | null;
  } | null;
  stream?: {
    uuid?: string | null;
    key?: string | null;
    name?: string | null;
  } | null;
  owner?: {
    uuid?: string | null;
    key?: string | null;
    name?: string | null;
    email?: string | null;
  } | null;
}

export interface ListResponse<T> {
  items: T[];
  next_page_token: string | null;
  previous_page_token: string | null;
  total_results: number | null;
}

export const emailApi = {
  list: async (params: {
    owner?: string;
    stream?: string;
    prospect?: string;
    types?: string[];
    search?: string;
    page_token?: string;
    page_size?: number;
    sort_field?: string;
    sort_direction?: 'asc' | 'desc';
  }): Promise<ListResponse<EmailListItem>> => {
    const response = await apiClient.get('/emails/', {
      params: {
        owner: params.owner,
        stream: params.stream,
        prospect: params.prospect,
        types: params.types,
        search: params.search,
        page_token: params.page_token,
        page_size: params.page_size ?? 50,
        sort_field: params.sort_field ?? 'send_date',
        sort_direction: params.sort_direction ?? 'desc',
      },
      paramsSerializer: {
        serialize: (p: any) => {
          const usp = new URLSearchParams();
          Object.entries(p).forEach(([key, value]) => {
            if (value === undefined || value === null) return;
            if (Array.isArray(value)) {
              value.forEach((v) => usp.append(`${key}[]`, String(v)));
            } else {
              usp.append(key, String(value));
            }
          });
          return usp.toString();
        },
      },
    });
    return response.data.output as ListResponse<EmailListItem>;
  },

  delete: async (emailUuid: string): Promise<void> => {
    await apiClient.delete(`/emails/${emailUuid}/`);
  },
};


